import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { ButtonComponent } from '@app/components/button/button.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { UploadedGeoImageService } from '../services/uploaded-geo-image.service';
import { UploadedGeoImageListItem } from '../services/uploaded-geo-image.models';
import { boundsOf, wktPolygonToLatLngRing } from '../utils/wkt.util';

/**
 * Full-size view of one uploaded geo-image with its metadata (FR-013). The image is the original photo, or
 * a raster's generated JPEG preview — browsers cannot render a GeoTIFF.
 */
@Component({
  selector: 'app-geo-image-detail-modal',
  standalone: true,
  templateUrl: './geo-image-detail-modal.component.html',
  styleUrls: ['./geo-image-detail-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, DialogComponent, ButtonComponent, PrimaryButton]
})
export class GeoImageDetailModalComponent {
  @Input() visible = false;
  @Input() item: UploadedGeoImageListItem | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() edit = new EventEmitter<UploadedGeoImageListItem>();
  @Output() delete = new EventEmitter<UploadedGeoImageListItem>();

  constructor(private readonly api: UploadedGeoImageService) {}

  get imageUrl(): string | null {
    return this.item ? this.api.getFullImageUrl(this.item) : null;
  }

  get regionPath(): string {
    return [this.item?.parentRegionName, this.item?.regionName].filter((part): part is string => Boolean(part)).join(' / ');
  }

  /** A raster has no single coordinate; its extent is shown as the south-west and north-east corners of its footprint. */
  get footprintExtent(): string | null {
    const bounds = boundsOf(wktPolygonToLatLngRing(this.item?.footprintWkt));
    if (!bounds) {
      return null;
    }
    const [[south, west], [north, east]] = bounds;
    const fixed = (value: number) => value.toFixed(4);
    return `${fixed(south)}, ${fixed(west)} — ${fixed(north)}, ${fixed(east)}`;
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  handleEdit(): void {
    if (this.item) {
      this.edit.emit(this.item);
    }
  }

  handleDelete(): void {
    if (this.item) {
      this.delete.emit(this.item);
    }
  }
}
